// Certify the customer's identity-document copy with the Ordem dos Advogados.
//
// Every copy certification is an act in the Ordem's online register. "Certify
// with the Ordem" fills the act in and stops on the register's confirmation
// page; the lawyer reads what the act will say and registers it, or cancels.
// The act's PDF then comes back onto the case for her to sign. Each half takes
// about a minute, so the panel shows the steps the service writes to the case
// (passport_certification_run) while it works.
//
// An act cannot be edited or withdrawn, so nothing here registers without the
// lawyer's click on what she read, and the backend registers only if the act
// would still get the number she saw.
//
// A child's case shows one panel per document: the child's and each parent's.

import React, { useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import {
  CertificationApproval,
  CertificationOutcome,
  CertificationRun,
  CertifiedPerson,
  PassportCertificationRecord,
  fetchPassportCertificationPdf,
  fetchPassportCertificationThumbnail,
  previewPassportCertification,
  registerPassportCertification,
} from '../../api/passportCertification';
import '../CertificationPage/CertificationPage.css';

// How often the case is re-read while a run is going, for its steps.
export const RUN_POLL_MS = 2000;
// A run still marked running after this long was cut off; the service's own
// limit is ten minutes.
const RUN_STALE_MS = 11 * 60 * 1000;

export interface PassportCertificationPanelProps {
  caseId: string;
  /** Whose document: the applicant (default), or a child's parent. */
  person?: CertifiedPerson;
  /** The panel's heading, e.g. "Ordem certification: parent 1". */
  title?: string;
  /** Whose document it is, shown until an act or a preview names it. */
  who?: string | null;
  record?: PassportCertificationRecord | null;
  run?: CertificationRun | null;
  /** Only the lawyer whose Ordem login registers the act may start one. */
  canCertify: boolean;
  user: User | null;
  /** Re-reads the case, which brings the run's newest steps. */
  onRefresh: () => void;
}

type Phase = 'idle' | 'previewing' | 'confirm' | 'registering';
type Message = { tone: 'error' | 'info'; text: string };

const isRunningElsewhere = (run?: CertificationRun | null): boolean => {
  if (!run || run.state !== 'running' || !run.started_at) return false;
  const started = Date.parse(run.started_at);
  return Number.isFinite(started) && Date.now() - started < RUN_STALE_MS;
};

/** What to tell the lawyer when a run did not end where she wanted it to. */
export const outcomeMessage = (outcome: CertificationOutcome, mode: 'preview' | 'register'): Message => {
  const reason = outcome.reason || 'no reason given';
  switch (outcome.status) {
    case 'approval_outdated':
      return { tone: 'error', text: `The register changed since the preview (${reason}). Nothing was registered. Preview it again.` };
    case 'refused':
      return { tone: 'error', text: `This case cannot be certified automatically: ${reason}.` };
    case 'needs_review':
      return { tone: 'error', text: `This needs a look in the register: ${reason}.` };
    case 'left_alone':
      return { tone: 'info', text: `Nothing was done: ${reason}.` };
    case 'busy':
      return { tone: 'info', text: 'Another certification is running right now. Try again in a minute.' };
    case 'forbidden':
      return { tone: 'error', text: reason };
    default:
      return mode === 'preview'
        ? { tone: 'error', text: `The preview stopped: ${reason}. Nothing was registered.` }
        : {
            tone: 'error',
            text: `It stopped: ${reason}. Run it again: an act that did go through is found in the register and attached, never registered twice.`,
          };
  }
};

/** "2026-11-03T14:06:12" as the Ordem prints it, "03/11/2026 14:06". Anything else as it came. */
export const registeredAt = (value: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(value);
  return match ? `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}` : value;
};

const approvalFrom = (preview: CertificationOutcome | null): CertificationApproval | null => {
  if (!preview?.number || !preview.description || typeof preview.sheets !== 'number') return null;
  return { number: preview.number, description: preview.description, sheets: preview.sheets };
};

const ActThumbnail: React.FC<{
  caseId: string;
  person: CertifiedPerson;
  user: User | null;
  version?: string;
  onOpen: () => void;
}> = ({ caseId, person, user, version, onOpen }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    let objectUrl: string | null = null;
    user
      .getIdToken()
      .then(token => fetchPassportCertificationThumbnail(token, caseId, person))
      .then(blob => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [caseId, person, user, version]);
  return (
    <button type="button" className="oa-cert-thumbnail" onClick={onOpen} aria-label="Open the act's PDF" title="Open PDF">
      {url ? <img src={url} alt="First page of the act" /> : <span aria-hidden="true">📄</span>}
    </button>
  );
};

const PassportCertificationPanel: React.FC<PassportCertificationPanelProps> = ({
  caseId,
  person = 'applicant',
  title = 'Ordem certification',
  who,
  record,
  run,
  canCertify,
  user,
  onRefresh,
}) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [preview, setPreview] = useState<CertificationOutcome | null>(null);
  const [message, setMessage] = useState<Message | null>(null);
  // The run the case held when this one started: its steps are not ours.
  const [previousRunStart, setPreviousRunStart] = useState<string | undefined>(undefined);
  const [opening, setOpening] = useState(false);

  const working = phase === 'previewing' || phase === 'registering';
  const elsewhere = !working && isRunningElsewhere(run);

  useEffect(() => {
    if (!working && !elsewhere) return undefined;
    const timer = setInterval(onRefresh, RUN_POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [working, elsewhere]);

  const ownRun = run && run.started_at !== previousRunStart ? run : null;
  const steps = phase === 'idle' ? (elsewhere ? run?.steps || [] : []) : ownRun?.steps || [];
  const stepsRunning = working || elsewhere;

  const start = async (next: 'previewing' | 'registering', call: (token: string) => Promise<CertificationOutcome>) => {
    if (!user) return;
    setPreviousRunStart(run?.started_at);
    setMessage(null);
    setPhase(next);
    let outcome: CertificationOutcome;
    try {
      outcome = await call(await user.getIdToken());
    } catch (err: any) {
      outcome = { status: 'failed', reason: err?.message || 'the portal could not start it' };
    }
    onRefresh();
    if (next === 'previewing' && outcome.status === 'preview') {
      setPreview(outcome);
      setPhase('confirm');
      return;
    }
    setPreview(null);
    setPhase('idle');
    if (outcome.status !== 'registered') setMessage(outcomeMessage(outcome, next === 'previewing' ? 'preview' : 'register'));
  };

  const certify = () => start('previewing', token => previewPassportCertification(token, caseId, person));

  const register = () => {
    const approval = approvalFrom(preview);
    if (!approval) {
      setPhase('idle');
      setMessage({ tone: 'error', text: 'The preview did not say what the act will be. Preview it again.' });
      return;
    }
    start('registering', token => registerPassportCertification(token, caseId, approval, person));
  };

  const cancel = () => {
    setPreview(null);
    setPhase('idle');
  };

  const withPdf = async (use: (url: string) => void, onError?: () => void) => {
    if (!user) return;
    setOpening(true);
    setMessage(null);
    try {
      const blob = await fetchPassportCertificationPdf(await user.getIdToken(), caseId, person);
      const url = URL.createObjectURL(blob);
      use(url);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) {
      onError?.();
      setMessage({ tone: 'error', text: err?.message || 'Could not load the act.' });
    } finally {
      setOpening(false);
    }
  };

  const openPdf = () => {
    // Opened before the download, while the click still allows a new tab.
    const tab = window.open('', '_blank');
    withPdf(
      url => {
        if (tab) tab.location.href = url;
        else window.open(url, '_blank');
      },
      // No blank tab left behind when the PDF could not be loaded.
      () => tab?.close()
    ).then(() => undefined);
  };

  const downloadPdf = () =>
    withPdf(url => {
      const link = document.createElement('a');
      link.href = url;
      link.download = `Ato_${(record?.registration_number || 'certificacao').replace(/\//g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    });

  const registered = record?.status === 'registered' && Boolean(record.gcs_path);
  if (!canCertify && !registered) return null;

  const badge = registered
    ? 'Registered'
    : phase === 'previewing' || elsewhere
      ? 'Preparing'
      : phase === 'confirm'
        ? 'Waiting for you'
        : phase === 'registering'
          ? 'Registering'
          : 'Not certified';
  const lastProblem =
    !registered && phase === 'idle' && !message && record && record.status !== 'registering'
      ? record.reason || record.error
      : null;
  const subject = record?.document?.holder
    ? `${record.document.holder}, document nº ${record.document.number}`
    : preview?.description || who || null;

  return (
    <div
      className={`poa-cert-panel ${registered ? 'poa-cert-panel--certified' : 'poa-cert-panel--pending'} oa-cert-panel`}
      data-testid={person === 'applicant' ? 'passport-certification' : `passport-certification-${person}`}
    >
      <div className="oa-cert-header">
        <div>
          <h3>{title}</h3>
          {subject && <p className="oa-cert-subject">{subject}</p>}
        </div>
        <span className={`oa-cert-badge oa-cert-badge--${registered ? 'done' : working || elsewhere ? 'busy' : phase}`}>
          {badge}
        </span>
      </div>

      {!registered && phase === 'idle' && !elsewhere && (
        <>
          <p>Registers the certification of the customer's document copy in the Ordem's register, under your login.</p>
          {lastProblem && <p className="oa-cert-last">Last attempt: {lastProblem}</p>}
          <button className="financas-btn financas-btn--primary" onClick={certify} disabled={!user}>
            Certify with the Ordem
          </button>
        </>
      )}

      {steps.length > 0 && (
        <ol className="oa-cert-steps" aria-live="polite">
          {steps.map((step, index) => {
            const current = stepsRunning && index === steps.length - 1;
            return (
              <li key={`${step.key}-${index}`} className={current ? 'oa-cert-step oa-cert-step--current' : 'oa-cert-step'}>
                <span className="oa-cert-step-mark" aria-hidden="true">{current ? '' : '✓'}</span>
                <span>
                  {step.text}
                  {current ? '…' : ''}
                  {step.detail ? <span className="oa-cert-step-detail"> {step.detail}</span> : null}
                </span>
              </li>
            );
          })}
        </ol>
      )}
      {working && steps.length === 0 && <p className="oa-cert-last">Starting…</p>}

      {phase === 'confirm' && preview && (
        <div className="oa-cert-confirm">
          {preview.adopted ? (
            <p>
              The register already holds act <strong>{preview.number}</strong> for this document. Attaching it registers
              nothing new.
            </p>
          ) : (
            <p>
              This is what the act will say. It gets number <strong>{preview.number}</strong> and cannot be undone.
            </p>
          )}
          {preview.resumo && <div className="oa-cert-resumo">{preview.resumo}</div>}
          <div className="oa-cert-actions">
            <button className="financas-btn financas-btn--primary" onClick={register}>
              {preview.adopted ? `Attach act ${preview.number}` : 'Register act'}
            </button>
            <button className="financas-btn" onClick={cancel}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {registered && record && (
        <div className="oa-cert-result">
          <ActThumbnail caseId={caseId} person={person} user={user} version={record.gcs_path} onOpen={openPdf} />
          <div>
            <dl className="oa-cert-facts">
              <dt>Act</dt>
              <dd>{record.registration_number}</dd>
              {record.consultation_code && (
                <>
                  <dt>Consultation code</dt>
                  <dd>{record.consultation_code}</dd>
                </>
              )}
              {record.registered_at && (
                <>
                  <dt>Registered at</dt>
                  <dd>{registeredAt(record.registered_at)}</dd>
                </>
              )}
            </dl>
            {record.adopted && <p className="oa-cert-last">Found in the register and attached; it was registered earlier.</p>}
            <div className="oa-cert-actions">
              <button className="financas-btn" onClick={openPdf} disabled={opening}>
                Open PDF
              </button>
              <button className="financas-btn financas-btn--primary" onClick={downloadPdf} disabled={opening}>
                Download to sign
              </button>
            </div>
            <p className="oa-cert-last">Next: sign it in Acrobat with your certificate.</p>
          </div>
        </div>
      )}

      {message && (
        <div className={message.tone === 'error' ? 'financas-office-error' : 'oa-cert-info'} role={message.tone === 'error' ? 'alert' : 'status'}>
          {message.text}
        </div>
      )}
    </div>
  );
};

export default PassportCertificationPanel;
